import { ChangeEvent, useState } from 'react';
import { Input, Select, Spinner } from '@iwoca/orion';
import { Trans, t } from '@lingui/macro';
import clsx from 'clsx';
import { DateTimePicker } from 'components/DatePicker/DateTimePicker';
import { EarlyRepaymentSchedule } from 'components/EarlyRepaymentSchedule/EarlyRepaymentSchedule';
import {
  addDays,
  addMonths,
  addYears,
  differenceInDays,
  endOfYesterday,
  isAfter,
  isBefore,
} from 'date-fns';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { useNumberFormat } from 'hooks/useNumberFormat';
import { TIntroducerApplication } from 'lendingApi/applications.types';
import { useGetDrawdownRepaymentSchedule } from 'lendingApi/lendingApiHooks';
import { dateToString } from 'utils/common/common';
import { getDurationInMonths } from 'utils/durationHelper';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';

import { CalculatorError } from './CalculatorError/CalculatorError';
import styles from './EarlyRepaymentCalculator.module.css';

type TProps = {
  formalOffer: TIntroducerApplication['formal_offer_sets'][number]['formal_offers'][number];
  monthlyPayments?: number;
};

export const EarlyRepaymentCalculator = ({
  formalOffer,
  monthlyPayments,
}: TProps) => {
  const defaultDate = dateToString(addMonths(new Date(), 1));
  const { formatCurrency } = useNumberFormat();
  const { trackEvent } = useAmplitude();
  const [amount, setAmount] = useState(formalOffer.max_amount);
  const [date, setDate] = useState<string | undefined>(defaultDate);
  const [selectedOption, setSelectedOption] = useState('1-month');
  const [overpaymentAmount, setOverpaymentAmount] = useState<number>();
  const [inputError, setInputError] = useState('');
  const { isDE } = useLocale();

  const { data, isFetching, isError, error } = useGetDrawdownRepaymentSchedule(
    amount,
    formalOffer,
    date,
    overpaymentAmount,
    monthlyPayments,
  );

  const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numberString = value.replace(/[^0-9]+/g, '');
    const amountNumber = parseInt(numberString, 10);

    if (!amountNumber) {
      return setAmount(0);
    }

    if (amountNumber > formalOffer.max_amount) {
      return setAmount(formalOffer.max_amount);
    }

    setAmount(amountNumber);
  };

  const handleOverpaymentChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputError('');
    const value = e.target.value;
    const numberString = value.replace(/[^0-9]+/g, '');
    const amountNumber = parseInt(numberString, 10);

    if (!amountNumber && amountNumber !== 0) {
      return setOverpaymentAmount(0);
    }

    if (amountNumber > formalOffer.max_amount) {
      return setOverpaymentAmount(formalOffer.max_amount);
    }

    if (monthlyPayments && amountNumber < monthlyPayments) {
      setInputError(
        t`Repayment amount should be higher than minimum monthly repayment`,
      );
    }

    setOverpaymentAmount(amountNumber);
  };

  const setDateWithTracking = (value: string, newDate?: Date) => {
    trackEvent(AMPLITUDE_EVENTS.earlyRepaymentCalculatorDateChanged, {
      Label: value,
      'Repayment duration (days)': newDate
        ? differenceInDays(newDate, new Date())
        : 'Full length',
    });

    if (!newDate) {
      // If date is undefined, API returns full length of repayment schedule
      setDate(undefined);
      return;
    }
    setDate(dateToString(newDate));
  };

  const handleRepaymentChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;

    switch (value) {
      case '1-month':
      case '3-months':
      case '6-months':
      case '9-months': {
        setSelectedOption(value);
        setOverpaymentAmount(undefined);
        // Get only the month from the value
        const newDate = addMonths(new Date(), parseInt(value.split('-')[0]));
        return setDateWithTracking(value, newDate);
      }
      case 'full-term': {
        setSelectedOption('full-term');
        setOverpaymentAmount(undefined);
        return setDateWithTracking(value, undefined);
      }
      case 'custom': {
        setSelectedOption('custom');
        setOverpaymentAmount(undefined);
        break;
      }
      case 'monthly-amount': {
        setDate(undefined);
        setSelectedOption('monthly-amount');
        break;
      }
    }
  };

  const getLoanEndDate = () => {
    switch (formalOffer.duration.unit) {
      case 'days':
        return addDays(new Date(), formalOffer.duration.amount);
      case 'months':
        return addMonths(new Date(), formalOffer.duration.amount);
      case 'years':
        return addYears(new Date(), formalOffer.duration.amount);
    }
  };

  const filterCustomDates = (date: Date) => {
    return isBefore(date, getLoanEndDate()) && isAfter(date, endOfYesterday());
  };

  const renderFullTermLabel = () => {
    return t`in ${getDurationInMonths(formalOffer.duration)} months`;
  };

  const renderValue = (label: string, value?: number) => {
    if ((!isFetching && !data) || isError) {
      return <p className={styles.summaryValue}>-</p>;
    }

    return (
      <>
        {isFetching || !data ? (
          <Spinner
            dataTestId={`${label}-valueSpinner`}
            className={styles.valueSpinner}
          />
        ) : (
          <p className={styles.summaryValue}>{formatCurrency(value)}</p>
        )}
      </>
    );
  };

  return (
    <div
      data-testid="early-repayment-calculator"
      className={styles.earlyRepaymentCalculator}
    >
      <h2 className={styles.heading}>
        <Trans>Calculate repayment</Trans>
      </h2>
      <div className={styles.repaymentOptions}>
        <div className={styles.repaymentOption}>
          <Trans>If the applicant borrows</Trans>
          <Input
            data-testid="amount-input"
            className={styles.repaymentOptionInput}
            onChange={handleAmountChange}
            value={formatCurrency(amount)}
          />
        </div>
        <div className={styles.repaymentOption}>
          <Trans>and repay</Trans>
          <div className={styles.dateInputs}>
            <Select
              data-testid="option-select"
              className={styles.repaymentOptionInput}
              onChange={handleRepaymentChange}
              defaultValue={selectedOption}
            >
              <option disabled value="">
                <Trans>Select an option</Trans>
              </option>
              <option value="1-month">
                <Trans>in 1 month</Trans>
              </option>
              <option value="6-months">
                <Trans>in 6 months</Trans>
              </option>
              <option value="9-months">
                <Trans>in 9 months</Trans>
              </option>
              <option value="full-term">{renderFullTermLabel()}</option>
              <option value="custom">
                <Trans>by custom date</Trans>
              </option>
              <option value="monthly-amount">
                <Trans>by monthly amount</Trans>
              </option>
            </Select>
            {selectedOption === 'custom' && (
              <div className={styles.customDate}>
                <DateTimePicker
                  filterDate={filterCustomDates}
                  handleChange={(date: Date | null) =>
                    date && setDate(dateToString(date))
                  }
                  withInput
                  defaultDate={defaultDate}
                />
              </div>
            )}
            {selectedOption === 'monthly-amount' && (
              <Input
                data-testid="montly-amount-input"
                className={styles.monthlyPaymentInput}
                onChange={handleOverpaymentChange}
                value={formatCurrency(overpaymentAmount)}
              />
            )}
          </div>
          {isDE && <div>zurückzahlt</div>}
        </div>
      </div>
      {(inputError || error) && <CalculatorError error={inputError || error} />}
      <div className={styles.repaymentValues}>
        <div className={styles.summaryItem}>
          <p className={styles.summaryHeading}>
            <Trans>Early repayment fee</Trans>:
          </p>
          {renderValue('repaymentFee', 0)}
        </div>
        <div className={styles.summaryItem}>
          <p className={styles.summaryHeading}>
            <Trans>Total fees + interest</Trans>:
          </p>
          {renderValue(
            'fees',
            (data?.data.summary.total_fees || 0) +
              (data?.data.summary.total_interest || 0),
          )}
        </div>
        <div className={styles.summaryItem}>
          <p className={styles.summaryHeading}>
            <Trans>Total repayment</Trans>:
          </p>
          {renderValue('totalRepayment', data?.data.summary.total_payable)}
        </div>
        <div className={clsx(styles.moneySaved, styles.summaryItem)}>
          <p className={styles.summaryHeading}>
            <Trans>Total savings</Trans>:
          </p>
          {renderValue('totalSavings', data?.data.summary.amount_saved || 0)}
        </div>
      </div>
      {data && (
        <EarlyRepaymentSchedule
          schedule={data?.data.schedule}
          summary={data?.data.summary}
          formalOffer={formalOffer}
          isCustomDate={selectedOption === 'custom'}
        />
      )}
    </div>
  );
};
